/**
 * Event ticket rules shared by the web app, the phone app, and the server
 * (Event Tickets plan 2026-10-19, U2). The web scanner, the app scanner, and
 * the check-in routes all decide "how many can come in" with these, so a
 * phone can never show a different number than the server enforces.
 *
 * The database functions in
 * gym-admin-dashboard/supabase/migrations/20261446_001_event_tickets_functions.sql
 * hold the same rules under a row lock; these are the screen's copy of them.
 *
 * Read by BOTH apps: no imports, no classes, no async, no array spread (see
 * docs/agent-context/mobile.md, shared modules).
 */

/** Stripe's card minimum. A paid ticket type costs at least this much (R3). */
export const MIN_PAID_TICKET_PRICE_CENTS = 50;

/** Matches the CHECK on event_ticket_types.price_cents. */
export const MAX_TICKET_PRICE_CENTS = 10000000;

/** Default for "most tickets one order can buy" (R38). */
export const DEFAULT_PER_ORDER_MAX = 10;

export type TicketPriceProblem = 'not_whole_cents' | 'negative' | 'below_minimum' | 'too_high';

/**
 * Why a ticket type price cannot be saved, or null when it can.
 * $0 is allowed: it makes the type staff-only (comps, R3).
 */
export function ticketTypePriceProblem(priceCents: number): TicketPriceProblem | null {
  if (!Number.isFinite(priceCents) || Math.floor(priceCents) !== priceCents) return 'not_whole_cents';
  if (priceCents < 0) return 'negative';
  if (priceCents === 0) return null;
  if (priceCents < MIN_PAID_TICKET_PRICE_CENTS) return 'below_minimum';
  if (priceCents > MAX_TICKET_PRICE_CENTS) return 'too_high';
  return null;
}

/** A $0 type is a staff-only comp and never shows to the public (R3). */
export function isPublicTicketType(type: { priceCents: number; archived?: boolean | null }): boolean {
  return !type.archived && type.priceCents > 0;
}

export interface TicketOrderCounts {
  quantity: number;
  admitted: number;
  refunded: number;
}

/** Tickets on an order that can still be used at the door. Never below zero. */
export function ticketsLeft(order: TicketOrderCounts): number {
  return Math.max(0, order.quantity - order.refunded - order.admitted);
}

export type AdmitProblem = 'not_whole' | 'none_left' | 'more_than_left';

/**
 * Why staff cannot admit `count` people on this order right now, or null when
 * they can (R18). Staff may admit fewer than are left; the rest stay valid.
 */
export function admitProblem(order: TicketOrderCounts, count: number): AdmitProblem | null {
  if (!Number.isFinite(count) || Math.floor(count) !== count || count < 1) return 'not_whole';
  const left = ticketsLeft(order);
  if (left === 0) return 'none_left';
  if (count > left) return 'more_than_left';
  return null;
}

/** The admit counts staff can pick from: 1 to the tickets left, or none. */
export function admitChoices(order: TicketOrderCounts): number[] {
  const left = ticketsLeft(order);
  const choices: number[] = [];
  for (let n = 1; n <= left; n += 1) choices.push(n);
  return choices;
}

export interface TicketRefundCounts extends TicketOrderCounts {
  kind: 'sale' | 'comp';
  /** Used tickets already refunded (money back, seat kept). */
  refundedUsed: number;
}

/**
 * Tickets on an order (or one line) that can still be refunded (KTD9). A sold
 * ticket may be refunded even after it was used at the door (owner decision
 * 2026-10-26): the money goes back, the seat stays taken. A free ticket can
 * only be removed while unused.
 */
export function ticketsRefundable(counts: TicketRefundCounts): number {
  if (counts.kind === 'comp') return ticketsLeft(counts);
  return Math.max(0, counts.quantity - counts.refunded - counts.refundedUsed);
}

export interface OfflineAdmit {
  clientEventId: string;
  quantity: number;
  /** ISO time the phone recorded the admit. */
  scannedAt: string;
}

export interface OfflineAdmitResult {
  clientEventId: string;
  admitted: number;
  excess: number;
}

export interface OfflineMergePreview {
  results: OfflineAdmitResult[];
  admitted: number;
  excess: number;
}

/**
 * What a sync will do to one order (KTD13): apply admits in scan order, cap at
 * the tickets left, and count the rest as excess for "Needs a look" (R24).
 * Ties on scan time break by client id, the same order the server uses.
 * Every admit is kept: they happened at the door, offline.
 */
export function previewOfflineMerge(order: TicketOrderCounts, admits: OfflineAdmit[]): OfflineMergePreview {
  const ordered = admits.slice().sort(function (a, b) {
    const ta = Date.parse(a.scannedAt);
    const tb = Date.parse(b.scannedAt);
    if (ta !== tb) return ta - tb;
    return a.clientEventId < b.clientEventId ? -1 : a.clientEventId > b.clientEventId ? 1 : 0;
  });
  let left = ticketsLeft(order);
  let admitted = 0;
  let excess = 0;
  const results: OfflineAdmitResult[] = [];
  for (let i = 0; i < ordered.length; i += 1) {
    const entry = ordered[i];
    const take = Math.max(0, Math.min(left, entry.quantity));
    left -= take;
    admitted += take;
    excess += entry.quantity - take;
    results.push({ clientEventId: entry.clientEventId, admitted: take, excess: entry.quantity - take });
  }
  return { results: results, admitted: admitted, excess: excess };
}

/** What a door scan or a typed code is (KTD7, R19). */
export type DoorScan =
  | { kind: 'ticket'; token: string }
  | { kind: 'poster'; eventId: string }
  | { kind: 'unknown' };

// KTD7: 32 random bytes in base64url is 43 characters. The upper bound only
// stops a garbage scan from reaching the server.
const DOOR_TOKEN = /^[A-Za-z0-9_-]{43,200}$/;
const DOOR_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function safeDecode(part: string): string {
  try {
    return decodeURIComponent(part);
  } catch {
    return '';
  }
}

/**
 * Read what the camera saw. A ticket QR is the order page URL
 * `https://<host>/t/<token>` (KTD7); a bare token also counts. The door
 * poster QR is the event's buy page `/programs/<gym>/events/<eventId>`, with
 * or without `/poster`. Anything else is unknown. The host is not checked:
 * the server decides whether a token is a real ticket of this gym.
 */
export function readDoorScan(raw: string): DoorScan {
  const text = (raw || '').trim();
  if (DOOR_TOKEN.test(text)) return { kind: 'ticket', token: text };
  const path = text.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/?#]*/i, '').split(/[?#]/)[0];
  const parts = path.split('/').filter(function (p) {
    return p.length > 0;
  });
  if (parts.length === 2 && parts[0] === 't') {
    const token = safeDecode(parts[1]);
    if (DOOR_TOKEN.test(token)) return { kind: 'ticket', token: token };
  }
  if (
    (parts.length === 4 || (parts.length === 5 && parts[4] === 'poster')) &&
    parts[0] === 'programs' &&
    parts[2] === 'events' &&
    DOOR_UUID.test(safeDecode(parts[3]))
  ) {
    return { kind: 'poster', eventId: safeDecode(parts[3]).toLowerCase() };
  }
  return { kind: 'unknown' };
}

// ── Door answers (U7, U8, U11) ──────────────────────────────────────────────
// Moved here from gym-admin-dashboard/src/utils/eventTickets/doorCheckin.ts
// (which re-exports them) so the web door page and the app scanner give the
// same answer for the same scan. The guest list never carries a ticket token:
// each guest has the SHA-256 of its token, so a phone can match a scanned code
// offline, but a saved list cannot be turned back into working tickets.

/** Order states the door list carries. Pending and failed orders are not tickets. */
export const DOOR_LIST_ORDER_STATUSES = ['paid', 'partially_refunded', 'refunded', 'oversold_refunded', 'cancelled_comp'] as const;
export type DoorOrderStatus = (typeof DOOR_LIST_ORDER_STATUSES)[number];

/**
 * Orders the door counts: their tickets not refunded unused (quantity -
 * refunded) and the people let in on them. A fully refunded order counts too:
 * since 2026-10-26 staff may refund used tickets, and those people stay
 * checked in (an order refunded before anyone came in counts 0 and 0).
 */
const DOOR_COUNTED_STATUSES: readonly string[] = ['paid', 'partially_refunded', 'refunded'];

export interface DoorGuest {
  order_id: string;
  /** The short code printed under the QR (ticketOrderCode). */
  code: string;
  /** SHA-256 hex of the order's token. Never the token itself. */
  token_hash: string;
  buyer_name: string;
  status: DoorOrderStatus;
  quantity: number;
  admitted: number;
  refunded: number;
  lines: DoorGuestLine[];
  /** The latest check-in that let someone in: who and when (R19). */
  last_admit: { by: string; at: string } | null;
}

/**
 * One ticket type on an order. line_id, type_id, and admitted came with the
 * door screen update (2026-10-23); a list saved on a phone before it has
 * none of them, so they are optional and read through the helpers below.
 */
export interface DoorGuestLine {
  line_id?: string;
  type_id?: string;
  name: string;
  quantity: number;
  refunded: number;
  /** People in on this line. The lines' admitted add up to the order's. */
  admitted?: number;
  /** Used tickets of this line refunded (money back, seat kept). Missing on lists saved before 2026-10-26. */
  refunded_used?: number;
}

export interface DoorEvent {
  id: string;
  title: string;
  start_time: string;
  end_time: string;
  is_all_day: boolean;
  time_zone: string;
  venue_name: string | null;
  venue_address: string | null;
  cancelled: boolean;
  /** Most tickets one order can buy (R38): the most an "Admit anyway" can let in. */
  per_order_max: number;
  /** The buy page and door poster while online sales are open (R26), else null. */
  sell: { buy_page: string; poster: string } | null;
  /** The gym location that hosts the event (KTD11). Missing on lists saved before 2026-10-23. */
  host_location_name?: string | null;
  /** The event's ticket types in the gym's order, for counts by type. Missing on older saved lists. */
  ticket_types?: Array<{ id: string; name: string }>;
}

/** One check-in that let people in, for the door screen's recent list. Names only (no emails). */
export interface DoorRecentCheckin {
  id: string;
  order_id: string;
  buyer_name: string;
  admitted: number;
  /** Which ticket types came in. Empty for check-ins made before 2026-10-23. */
  lines: Array<{ type_id: string; count: number }>;
  at: string;
  by: string;
}

export interface DoorGuestList {
  event: DoorEvent;
  guests: DoorGuest[];
  /** When the server built this list (ISO). */
  generated_at: string;
  /** The latest check-ins, newest first. Missing on older saved lists. */
  recent?: DoorRecentCheckin[];
}

/** What a door scan means (R18, R19, R25). */
export type DoorAnswer =
  | { result: 'valid'; guest: DoorGuest }
  | { result: 'used'; guest: DoorGuest }
  | { result: 'refunded'; guest: DoorGuest }
  | { result: 'other_event'; event_title: string; event_start: string; time_zone: string; buyer_name: string; code: string }
  | { result: 'poster' }
  | { result: 'event_cancelled' }
  | { result: 'unknown' };

/** One guest's door answer from its own counts. */
export function doorAnswerForGuest(guest: DoorGuest): Extract<DoorAnswer, { guest: DoorGuest }> {
  if (DOOR_COUNTED_STATUSES.indexOf(guest.status) === -1) return { result: 'refunded', guest: guest };
  if (ticketsLeft(guest) > 0) return { result: 'valid', guest: guest };
  // Nothing left: used when anyone came in on it, else every ticket was refunded.
  return guest.admitted > 0 ? { result: 'used', guest: guest } : { result: 'refunded', guest: guest };
}

/** "Checked in X of Y tickets": tickets that can come in, comps included (R17). */
export function doorCounts(guests: DoorGuest[]): { checkedIn: number; tickets: number } {
  let checkedIn = 0;
  let tickets = 0;
  for (let i = 0; i < guests.length; i += 1) {
    const g = guests[i];
    if (DOOR_COUNTED_STATUSES.indexOf(g.status) === -1) continue;
    checkedIn += g.admitted;
    tickets += g.quantity - g.refunded;
  }
  return { checkedIn: checkedIn, tickets: tickets };
}

/** An admit made on this phone that the server has not confirmed yet (KTD13). */
export interface DoorOutboxEntry {
  client_event_id: string;
  /** The order, when the phone's list knew it. */
  order_id: string | null;
  /** The scanned code, only for "Admit anyway" on a code the list did not know (R25). */
  token: string | null;
  quantity: number;
  scanned_at: string;
  /** Which lines came in, {line_id: count}, adding up to quantity. Null when the phone could not tell. */
  line_counts?: Record<string, number> | null;
}

/**
 * The guest list as this phone sees it: the server's counts plus this
 * phone's admits still waiting to send. The phone may show fewer left than
 * the server will allow; it never shows more.
 */
export function guestsWithPendingAdmits(guests: DoorGuest[], outbox: DoorOutboxEntry[]): DoorGuest[] {
  if (outbox.length === 0) return guests;
  const pending: Record<string, DoorOutboxEntry[]> = {};
  for (let i = 0; i < outbox.length; i += 1) {
    const e = outbox[i];
    if (!e.order_id) continue;
    if (!Object.prototype.hasOwnProperty.call(pending, e.order_id)) pending[e.order_id] = [];
    pending[e.order_id].push(e);
  }
  return guests.map(function (g) {
    const entries = Object.prototype.hasOwnProperty.call(pending, g.order_id) ? pending[g.order_id] : null;
    if (!entries) return g;
    let next = g;
    for (let i = 0; i < entries.length; i += 1) {
      // Capped so the counts stay possible; the server records the excess (R24).
      const take = Math.min(ticketsLeft(next), entries[i].quantity);
      if (take <= 0) continue;
      next = withAdmitted(next, take, allocateDoorLines(next.lines, entries[i].line_counts || null, take));
    }
    return next;
  });
}

// ── Ticket types at the door (door screen update, 2026-10-23) ───────────────
// The database's event_ticket_take_lines (migration 20261510_001) holds the
// same rule under the order's lock; these are the screen's copy of it.

/** Tickets left on one line. Never below zero. */
export function doorLineLeft(line: DoorGuestLine): number {
  return Math.max(0, line.quantity - line.refunded - (line.admitted || 0));
}

/**
 * Put up to `max` people on an order's lines: with a breakdown, each line
 * takes at most what it asks for; without one, lines fill in list order (the
 * server sends them highest price first). Never more than a line's tickets
 * left. Returns {line_id: count}.
 */
export function allocateDoorLines(lines: DoorGuestLine[], counts: Record<string, number> | null, max: number): Record<string, number> {
  const out: Record<string, number> = {};
  let remaining = Math.max(0, max);
  for (let i = 0; i < lines.length && remaining > 0; i += 1) {
    const line = lines[i];
    if (!line.line_id) continue;
    const want = counts ? counts[line.line_id] || 0 : remaining;
    const take = Math.min(want, doorLineLeft(line), remaining);
    if (take > 0) {
      out[line.line_id] = take;
      remaining -= take;
    }
  }
  return out;
}

/** A guest with `count` more people in, put on its lines as `taken` says. */
export function withAdmitted(guest: DoorGuest, count: number, taken: Record<string, number>): DoorGuest {
  return Object.assign({}, guest, {
    admitted: Math.min(guest.quantity - guest.refunded, guest.admitted + count),
    lines: guest.lines.map(function (l) {
      const add = l.line_id && taken[l.line_id] ? taken[l.line_id] : 0;
      return add ? Object.assign({}, l, { admitted: (l.admitted || 0) + add }) : l;
    }),
  });
}

/** The server's counts for one order (an admit or sync answer) put on a guest. */
export function withServerCounts(guest: DoorGuest, admitted: number, lines: Array<{ line_id: string; admitted: number }> | null | undefined): DoorGuest {
  const byLine: Record<string, number> = {};
  const list = lines || [];
  for (let i = 0; i < list.length; i += 1) byLine[list[i].line_id] = list[i].admitted;
  return Object.assign({}, guest, {
    admitted: Math.min(guest.quantity - guest.refunded, admitted),
    lines: guest.lines.map(function (l) {
      return l.line_id && Object.prototype.hasOwnProperty.call(byLine, l.line_id) ? Object.assign({}, l, { admitted: byLine[l.line_id] }) : l;
    }),
  });
}

/** A sync answer's counts for every order it named, put on the saved guests. */
export function guestsWithSyncedCounts(
  guests: DoorGuest[],
  orders: Array<{ order_id: string; admitted: number; lines?: Array<{ line_id: string; admitted: number }> }>
): DoorGuest[] {
  if (orders.length === 0) return guests;
  const byId: Record<string, { admitted: number; lines?: Array<{ line_id: string; admitted: number }> }> = {};
  for (let i = 0; i < orders.length; i += 1) byId[orders[i].order_id] = orders[i];
  return guests.map(function (g) {
    const o = Object.prototype.hasOwnProperty.call(byId, g.order_id) ? byId[g.order_id] : null;
    return o ? withServerCounts(g, o.admitted, o.lines) : g;
  });
}

/**
 * What the check-in sheet starts with. "types" when two or more ticket types
 * still have tickets left: one +/- per type, each filled with its tickets
 * left, so a whole group is still one tap. Otherwise one count.
 */
export function doorAdmitPicks(guest: DoorGuest): { mode: 'types' | 'count'; picks: Record<string, number> } {
  const open = guest.lines.filter(function (l) {
    return doorLineLeft(l) > 0;
  });
  const known = open.every(function (l) {
    return Boolean(l.line_id);
  });
  if (open.length < 2 || !known) return { mode: 'count', picks: {} };
  return { mode: 'types', picks: allocateDoorLines(guest.lines, null, ticketsLeft(guest)) };
}

/** People a breakdown checks in. */
export function doorPickTotal(picks: Record<string, number>): number {
  let n = 0;
  for (const k in picks) if (Object.prototype.hasOwnProperty.call(picks, k)) n += picks[k] || 0;
  return n;
}

/** A breakdown with no zero lines, or null when it lets nobody in. */
export function doorLineCounts(picks: Record<string, number>): Record<string, number> | null {
  const out: Record<string, number> = {};
  let any = false;
  for (const k in picks) {
    if (Object.prototype.hasOwnProperty.call(picks, k) && picks[k] > 0) {
      out[k] = picks[k];
      any = true;
    }
  }
  return any ? out : null;
}

export interface DoorTypeCount {
  key: string;
  name: string;
  checkedIn: number;
  tickets: number;
}

/**
 * Live tickets, people in, and tickets sold per ticket type, in the gym's
 * type order (types unknown to the list go last). Sold leaves out used
 * tickets refunded after the door; tickets and people in keep them.
 */
function aggregateDoorTypes(guests: DoorGuest[], types: Array<{ id: string; name: string }> | null | undefined): Array<DoorTypeCount & { sold: number }> {
  const rows: Record<string, DoorTypeCount & { sold: number }> = {};
  const order: string[] = [];
  const known = types || [];
  for (let i = 0; i < known.length; i += 1) {
    rows[known[i].id] = { key: known[i].id, name: known[i].name, checkedIn: 0, tickets: 0, sold: 0 };
    order.push(known[i].id);
  }
  for (let i = 0; i < guests.length; i += 1) {
    const g = guests[i];
    if (DOOR_COUNTED_STATUSES.indexOf(g.status) === -1) continue;
    for (let j = 0; j < g.lines.length; j += 1) {
      const l = g.lines[j];
      const key = l.type_id || 'name:' + l.name;
      if (!Object.prototype.hasOwnProperty.call(rows, key)) {
        rows[key] = { key: key, name: l.name, checkedIn: 0, tickets: 0, sold: 0 };
        order.push(key);
      }
      const live = Math.max(0, l.quantity - l.refunded);
      rows[key].tickets += live;
      rows[key].checkedIn += Math.min(l.admitted || 0, live);
      rows[key].sold += Math.max(0, live - (l.refunded_used || 0));
    }
  }
  return order.map(function (k) {
    return rows[k];
  });
}

/**
 * "Adult 35 of 73 in", one row per ticket type with tickets, in the gym's
 * type order. Live tickets only, like doorCounts. Empty when the event has
 * fewer than two such types (the total already says it).
 */
export function doorTypeCounts(guests: DoorGuest[], types: Array<{ id: string; name: string }> | null | undefined): DoorTypeCount[] {
  const out = aggregateDoorTypes(guests, types)
    .filter(function (r) {
      return r.tickets > 0;
    })
    .map(function (r) {
      return { key: r.key, name: r.name, checkedIn: r.checkedIn, tickets: r.tickets };
    });
  return out.length >= 2 ? out : [];
}

/** Tickets sold per type for the event details, live tickets only, in the gym's type order. */
export function doorTypeSales(guests: DoorGuest[], types: Array<{ id: string; name: string }> | null | undefined): Array<{ key: string; name: string; sold: number }> {
  return aggregateDoorTypes(guests, types).map(function (r) {
    return { key: r.key, name: r.name, sold: r.sold };
  });
}

/** Where a group is at the door. "void" is a refunded or cancelled ticket. */
export type DoorGuestState = 'none' | 'part' | 'full' | 'void';

export function doorGuestState(guest: DoorGuest): DoorGuestState {
  const answer = doorAnswerForGuest(guest);
  if (answer.result === 'refunded') return 'void';
  if (answer.result === 'used') return 'full';
  return guest.admitted > 0 ? 'part' : 'none';
}

export type DoorGuestFilter = 'all' | 'none' | 'part' | 'full';

/** The guest list's filter chips, in screen order. */
export const DOOR_GUEST_FILTERS: Array<{ key: DoorGuestFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'none', label: 'Not here yet' },
  { key: 'part', label: 'Some in' },
  { key: 'full', label: 'All in' },
];

/** The guest list for one chip and a search, A to Z. */
export function filterDoorGuests(guests: DoorGuest[], filter: DoorGuestFilter, query: string): DoorGuest[] {
  const found = searchDoorGuests(guests, query);
  if (filter === 'all') return found;
  return found.filter(function (g) {
    return doorGuestState(g) === filter;
  });
}

/** How many guests each chip shows, before any search. */
export function doorFilterCounts(guests: DoorGuest[]): Record<DoorGuestFilter, number> {
  const out: Record<DoorGuestFilter, number> = { all: guests.length, none: 0, part: 0, full: 0 };
  for (let i = 0; i < guests.length; i += 1) {
    const s = doorGuestState(guests[i]);
    if (s !== 'void') out[s] += 1;
  }
  return out;
}

/** One row of the recent check-ins list. */
export interface DoorFeedItem {
  key: string;
  buyer_name: string;
  admitted: number;
  /** "1 Adult, 1 Child", or '' when the types are not known. */
  types: string;
  at: string;
  by: string;
  /** Made on this phone and not sent yet. */
  waiting: boolean;
}

/** line_id -> type_id for the guests' lines (one order only when `orderId` is given). */
function lineTypeMap(guests: DoorGuest[], orderId: string | null): Record<string, string> {
  const out: Record<string, string> = {};
  for (let i = 0; i < guests.length; i += 1) {
    const g = guests[i];
    if (orderId && g.order_id !== orderId) continue;
    for (let j = 0; j < g.lines.length; j += 1) {
      const l = g.lines[j];
      if (l.line_id && l.type_id) out[l.line_id] = l.type_id;
    }
  }
  return out;
}

/** {line_id: count} as {type_id: count}; lines the map does not know are left out. */
function countsByType(lineCounts: Record<string, number> | null | undefined, lineType: Record<string, string>): Record<string, number> {
  const out: Record<string, number> = {};
  const lc = lineCounts || {};
  for (const lineId in lc) {
    if (Object.prototype.hasOwnProperty.call(lc, lineId) && lineType[lineId] && lc[lineId] > 0) out[lineType[lineId]] = (out[lineType[lineId]] || 0) + lc[lineId];
  }
  return out;
}

/** "2 Adult, 1 Child" from {type_id: count}, in the gym's type order. */
function typesLine(counts: Record<string, number>, types: Array<{ id: string; name: string }>): string {
  const parts: string[] = [];
  for (let i = 0; i < types.length; i += 1) {
    const n = counts[types[i].id] || 0;
    if (n > 0) parts.push(n + ' ' + types[i].name);
  }
  return parts.join(', ');
}

/**
 * The recent check-ins list: this phone's check-ins waiting to send on top,
 * newest first, then the saved list's. At most `max` rows.
 */
export function doorRecentFeed(list: DoorGuestList | null, outbox: DoorOutboxEntry[], max: number): DoorFeedItem[] {
  if (!list) return [];
  const types = list.event.ticket_types || [];
  const lineType = lineTypeMap(list.guests, null);
  const nameByOrder: Record<string, string> = {};
  for (let i = 0; i < list.guests.length; i += 1) nameByOrder[list.guests[i].order_id] = list.guests[i].buyer_name;
  const items: DoorFeedItem[] = [];
  const waiting = outbox.slice().sort(function (a, b) {
    return Date.parse(b.scanned_at) - Date.parse(a.scanned_at);
  });
  for (let i = 0; i < waiting.length; i += 1) {
    const e = waiting[i];
    const byType = countsByType(e.line_counts, lineType);
    items.push({
      key: 'out:' + e.client_event_id,
      buyer_name: e.order_id && nameByOrder[e.order_id] ? nameByOrder[e.order_id] : DOOR_LIST_COPY.CODE_NOT_ON_LIST,
      admitted: e.quantity,
      types: typesLine(byType, types),
      at: e.scanned_at,
      by: DOOR_LIST_COPY.YOU,
      waiting: true,
    });
  }
  const recent = list.recent || [];
  for (let i = 0; i < recent.length; i += 1) {
    const r = recent[i];
    const byType: Record<string, number> = {};
    for (let j = 0; j < r.lines.length; j += 1) byType[r.lines[j].type_id] = (byType[r.lines[j].type_id] || 0) + r.lines[j].count;
    items.push({ key: 'srv:' + r.id, buyer_name: r.buyer_name, admitted: r.admitted, types: typesLine(byType, types), at: r.at, by: r.by, waiting: false });
  }
  return items.slice(0, max);
}

/** How many recent check-ins a phone keeps and shows. */
export const DOOR_RECENT_MAX = 50;

/**
 * The saved list with this phone's just-made online check-in on top of the
 * recent list, until the next fresh list brings the server's own row.
 */
export function listWithRecentAdmit(
  list: DoorGuestList,
  entry: { id: string; order_id: string; buyer_name: string; admitted: number; line_counts: Record<string, number>; at: string }
): DoorGuestList {
  const byType = countsByType(entry.line_counts, lineTypeMap(list.guests, entry.order_id));
  const lines: Array<{ type_id: string; count: number }> = [];
  for (const typeId in byType) if (Object.prototype.hasOwnProperty.call(byType, typeId)) lines.push({ type_id: typeId, count: byType[typeId] });
  const row: DoorRecentCheckin = { id: entry.id, order_id: entry.order_id, buyer_name: entry.buyer_name, admitted: entry.admitted, lines: lines, at: entry.at, by: DOOR_LIST_COPY.YOU };
  return Object.assign({}, list, { recent: [row].concat(list.recent || []).slice(0, DOOR_RECENT_MAX) });
}

/** A map search for the venue, or null when the event has no venue to find. */
export function doorMapsUrl(event: Pick<DoorEvent, 'venue_name' | 'venue_address'>): string | null {
  const q = [event.venue_name, event.venue_address]
    .filter(function (p) {
      return Boolean(p && p.trim());
    })
    .join(', ');
  return q ? 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(q) : null;
}

/** The guest list, recent check-ins, and event details words, the same on the web page and in the app. */
export const DOOR_LIST_COPY = {
  GUEST_LIST: 'Guest list',
  SEARCH: 'Search name or code',
  IN_OF: function (inNow: number, of: number) { return inNow + ' of ' + of + ' in'; },
  LEFT: function (n: number) { return n + ' left'; },
  NOT_YET: 'Not here yet',
  NO_MATCH: 'No one matches.',
  RECENT: 'Recent check-ins',
  RECENT_EMPTY: 'No one is checked in yet.',
  SEE_ALL: function (n: number) { return 'See all ' + n; },
  SHOW_LESS: 'Show less',
  WAITING: 'Waiting to send',
  IN_COUNT: function (n: number) { return n + ' in'; },
  BY: function (by: string) { return 'by ' + by; },
  YOU: 'you',
  CODE_NOT_ON_LIST: 'Code not on the list',
  WHO_IS_HERE: 'Who is here now?',
  BY_TYPE: 'Checked in by ticket type',
  DETAILS: 'Details',
  EVENT_DETAILS: 'Event details',
  DATE: 'Date',
  TIME: 'Time',
  VENUE: 'Venue',
  OPEN_MAPS: 'Open in Maps',
  HOSTED_BY: 'Hosted by',
  TICKET_TYPES: 'Ticket types',
  SOLD: function (n: number) { return n + ' sold'; },
  SAVED_LIST: 'From the list saved on this phone.',
  DONE: 'Done',
} as const;


/** Repeat reads of the same code this close together are one scan (U8). */
export const DOOR_REPEAT_SCAN_MS = 3000;

export function isRepeatScan(last: { value: string; at: number } | null, value: string, now: number): boolean {
  return Boolean(last && last.value === value && now - last.at < DOOR_REPEAT_SCAN_MS);
}

/** How often an open door screen refreshes the guest list while online (U8). */
export const DOOR_LIST_REFRESH_MS = 60000;

/** "List updated just now" / "List updated 4 min ago" / "List updated 2 hr ago". */
export function listUpdatedLabel(updatedAtMs: number | null, nowMs: number): string {
  if (updatedAtMs == null) return 'List not saved on this phone yet';
  const minutes = Math.max(0, Math.floor((nowMs - updatedAtMs) / 60000));
  if (minutes < 1) return 'List updated just now';
  if (minutes < 60) return 'List updated ' + minutes + ' min ago';
  return 'List updated ' + Math.floor(minutes / 60) + ' hr ago';
}

/** Search the saved list by name or printed code (R20). */
export function searchDoorGuests(guests: DoorGuest[], query: string): DoorGuest[] {
  const q = query.trim().toLowerCase();
  const codeQ = q.replace(/[^a-z0-9]/g, '');
  const sorted = guests.slice().sort(function (a, b) {
    return a.buyer_name.localeCompare(b.buyer_name);
  });
  if (!q) return sorted;
  return sorted.filter(function (g) {
    return g.buyer_name.toLowerCase().indexOf(q) !== -1 || (codeQ.length > 0 && g.code.toLowerCase().replace(/-/g, '').indexOf(codeQ) !== -1);
  });
}

/** A saved guest list is cleared this long after the event ends (U8). */
export const DOOR_LIST_KEEP_AFTER_END_MS = 24 * 3600000;

export function doorListExpired(eventEndIso: string, nowMs: number): boolean {
  const end = Date.parse(eventEndIso);
  return Number.isFinite(end) && nowMs > end + DOOR_LIST_KEEP_AFTER_END_MS;
}

/**
 * What a door phone says after a scan or a name pick (R18, R19, R25): the web
 * door page and the app scanner show the same words.
 */
export const DOOR_ANSWER_COPY = {
  GOOD: 'Good ticket',
  LEFT: function (n: number) { return `${n} left on this ticket`; },
  ALREADY_IN: function (n: number) { return `${n} already in.`; },
  HOW_MANY: 'How many are here now?',
  CHECK_IN: function (n: number) { return `Check in ${n}`; },
  USED: 'Already used',
  USED_ALL: function (n: number) { return `All ${n} checked in.`; },
  USED_BY: function (by: string, at: string) { return `By ${by}, ${at}.`; },
  REFUNDED: 'Refunded',
  CANCELLED_TICKET: 'Ticket cancelled',
  DO_NOT_ADMIT: 'Do not let them in.',
  OTHER_EVENT: 'Wrong event',
  OTHER_EVENT_LINE: function (title: string, date: string) { return `This ticket is for ${title} on ${date}.`; },
  POSTER: 'This is the buy poster',
  POSTER_LINE: 'It is not a ticket. Ask for the QR code from their ticket email.',
  CANCELLED: 'Event cancelled',
  CANCELLED_LINE: 'Do not check anyone in.',
  UNKNOWN: 'Not a ticket',
  UNKNOWN_LINE: 'This code is not a ticket for this gym. Do not let them in.',
  NOT_ON_LIST: 'Not on this phone’s list',
  NOT_ON_LIST_LINE: 'With no signal, this phone can only check tickets on its saved list. A ticket bought in the last few minutes is not on it yet.',
  ADMIT_ANYWAY: function (n: number) { return `Admit ${n} anyway`; },
  ADMIT_ANYWAY_NOTE: 'It is checked when signal returns. If it was not a ticket, it is saved for the gym to review.',
  NO_LIST: 'No guest list on this phone',
  NO_LIST_LINE: 'Connect to the internet once to load the list.',
  NEXT: 'Scan next',
  CANCEL: 'Cancel',
} as const;

function checkInsWord(n: number): string {
  return n + ' ' + (n === 1 ? 'check-in' : 'check-ins');
}

/** The door screen's status and result lines, the same on the web page and in the app. */
export const DOOR_STATUS_COPY = {
  CHECKED_IN_OF: function (of: number) { return 'checked in of ' + of + ' ' + (of === 1 ? 'ticket' : 'tickets'); },
  ONLINE: 'Online',
  SENDING: 'Sending check-ins…',
  OFFLINE: 'No signal · check-ins save on this phone',
  WAITING: function (n: number) { return checkInsWord(n) + ' waiting to send'; },
  OFFLINE_WAITING: function (n: number) { return 'No signal · ' + checkInsWord(n) + ' waiting to send'; },
  SENT: function (n: number) { return checkInsWord(n) + ' sent.'; },
  SENT_FLAGGED: function (n: number) { return n + ' over the ticket count ' + (n === 1 ? 'was' : 'were') + ' saved for the gym to review.'; },
  ADMITTED: function (n: number, left: number) { return left > 0 ? n + ' checked in. ' + left + ' left on this ticket.' : n + ' checked in. Ticket fully used.'; },
  ADMITTED_OFFLINE: function (n: number) { return n + ' checked in on this phone. It sends on its own.'; },
  NONE_LEFT: 'No tickets left on this order. Someone already used them.',
  TYPE_SHORT: 'Not that many tickets are left for what you picked. Check the counts and try again.',
  NOT_ON_LIST: 'You are not on the check-in list for this event. Ask an owner or admin to add you.',
  NOT_FOUND: 'This event does not sell tickets.',
  NO_LIST_OFFLINE: 'No signal, and this phone has no saved guest list for this event yet. Connect once to load it.',
  SEARCH_EMPTY: 'No match.',
  ALL_IN: 'All in',
  REFUNDED: 'Refunded',
} as const;

/**
 * Whether a door route reply really came from CheerVue. Every answer from the
 * check-in routes is a JSON object, and every 200 carries `success: true`.
 * Anything else (a venue wifi sign-in page, a proxy's error page, at any
 * status) is no signal to a door phone: the waiting check-ins stay waiting
 * and the saved list is kept, never deleted as if CheerVue had refused it.
 * CheerVue's own server error (a JSON 5xx) is a real answer: the phone still
 * has signal. The door hooks keep an admit that got one and resend it with
 * the same id (the server may have saved it before it failed), keep the
 * saved list, and stay online.
 */
export function isDoorReplyFromServer(status: number, body: unknown): boolean {
  if (typeof body !== 'object' || body === null || Array.isArray(body)) return false;
  return status !== 200 || (body as { success?: unknown }).success === true;
}

/** A list route answer whose shape can replace the phone's saved guest list. */
export function isDoorGuestListBody(body: unknown): boolean {
  if (typeof body !== 'object' || body === null) return false;
  const b = body as { guests?: unknown; event?: unknown };
  const event = b.event as { end_time?: unknown } | null | undefined;
  return Array.isArray(b.guests) && typeof event === 'object' && event !== null && typeof event.end_time === 'string';
}

/** Why an online check-in was refused, the same on the web page and in the app. */
export const DOOR_REFUSAL_COPY = {
  NOT_ON_LIST: 'You are not on the check-in list for this event.',
  CANCELLED: 'This event was cancelled. Do not check anyone in.',
  CANNOT_USE: 'This ticket cannot be used. Do not let them in.',
  TRY_AGAIN: 'Could not check them in. Try again.',
} as const;
